import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Plus,
    X,
    Edit2,
    Trash2,
    ChevronLeft,
    ChevronRight,
    Package,
    ShieldCheck,
    List,
    LayoutGrid,
    BookOpen,
    Receipt,
    MoreHorizontal,
    Eye
} from 'lucide-react';
import { Card, Badge, ConfirmationModal, toast } from '../components/v2/V2Components';
import type { BadgeVariant } from '../components/v2/V2Components';
import InventoryService from '../services/InventoryService';
import { getItemImageUrl } from '../constants/images';
import usePageView from '../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../constants/analyticsEvents';
import type { Item, Room, DocumentIndicators } from '../types/item';
import { getCategoryIcon, formatCategory, getItemValueNum } from './inventory/itemHelpers';

/** A status badge derived from an item's warranty/maintenance state, shaped to
 *  the <Badge> component's props. Lives here (UI layer) rather than in the
 *  domain types so types/item.ts stays free of UI-component coupling. */
interface ItemStatusBadge {
    label: string;
    variant: BadgeVariant;
}

const ITEMS_PER_PAGE = 10;

const getItemStatus = (item: Item): ItemStatusBadge[] => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const badges: ItemStatusBadge[] = [];

    // Warranty check
    if (item.warranty_expiration) {
        // Parse the date-only portion as LOCAL midnight to avoid TZ shift
        // (new Date("2026-12-31") parses as UTC midnight, then setHours(0,…)
        // pulls Dec 31 → Dec 30 for users in negative offsets). PR #199 fix.
        const [year, month, day] = item.warranty_expiration
            .split('T')[0]
            .split('-')
            .map(Number);
        const warrantyDate = new Date(year, month - 1, day);
        const daysUntilExpiry = Math.round((warrantyDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry > 30) {
            badges.push({ label: 'Active', variant: 'Active Maintenance' });
        } else if (daysUntilExpiry > 0) {
            badges.push({ label: 'Warranty Exp.', variant: 'Warning' });
        }
    }

    // Maintenance-due badges are now sourced from the Task system, not
    // standalone item.maintenance_interval_days / last_maintenance_date
    // columns (removed in db_migrate_drop_item_legacy_columns). If we want
    // the badge back, query the item's overdue Tasks via the inventory
    // list endpoint and surface them here.

    if (badges.length === 0) {
        badges.push({ label: 'Active', variant: 'Active Maintenance' });
    }

    return badges;
};

// Check which document types exist for an item. The list endpoint sends
// document presence as has_* booleans (no documents array) — the full
// documents list is detail-only, fetched via GET /items/:id.
const getDocumentIndicators = (item: Item): DocumentIndicators => ({
    manual: !!item.has_manual,
    receipt: !!item.has_receipt,
    warranty: !!item.has_warranty,
});

const Inventory = () => {
    usePageView(ANALYTICS_EVENTS.INVENTORY_VIEWED);
    const navigate = useNavigate();
    const [items, setItems] = useState<Item[]>([]);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    // View mode
    const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
    const [currentPage, setCurrentPage] = useState(1);

    // Action menu & delete confirmation
    const [actionMenuItemId, setActionMenuItemId] = useState<number | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Item | null>(null);

    // Available categories
    const categories = ['All', 'Appliance', 'Furniture', 'Electronics', 'HVAC', 'Plumbing', 'Security', 'Other'];

    useEffect(() => {
        fetchData();
    }, []);

    // Close action menu on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            // e.target can be a Text node or any EventTarget; only Element has .closest().
            const target = e.target;
            if (!(target instanceof Element)) return;
            if (actionMenuItemId && !target.closest('[data-action-menu]')) {
                setActionMenuItemId(null);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [actionMenuItemId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [itemsData, roomsData] = await Promise.all([
                InventoryService.getAllItems(),
                InventoryService.getRooms()
            ]);
            setItems(Array.isArray(itemsData) ? itemsData : []);
            setRooms(Array.isArray(roomsData) ? roomsData : []);
        } catch (err) {
            console.error('Failed to fetch inventory:', err);
            setError('Failed to load inventory');
        } finally {
            setLoading(false);
        }
    };

    const getRoomName = (roomId?: number | null): string => {
        const room = rooms.find(r => r.id === roomId);
        return room?.name || 'Unknown';
    };

    // Filter items based on search and category
    const filteredItems = useMemo(() => {
        const query = searchQuery.toLowerCase();
        const selCat = selectedCategory.toLowerCase();
        return items.filter(item => {
            const matchesSearch = !query ||
                item.name?.toLowerCase().includes(query) ||
                item.manufacturer?.toLowerCase().includes(query) ||
                item.model_number?.toLowerCase().includes(query) ||
                item.serial_number?.toLowerCase().includes(query);

            const itemCat = (item.item_category || item.category || 'Other').toLowerCase();
            const matchesCategory = selectedCategory === 'All' || itemCat === selCat;

            return matchesSearch && matchesCategory;
        });
    }, [items, searchQuery, selectedCategory]);

    // Total catalogued valuation, surfaced in the header
    const totalValue = useMemo(() =>
        items.reduce((acc, item) => acc + getItemValueNum(item), 0),
        [items]
    );

    // Pagination
    const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredItems.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredItems, currentPage]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedCategory]);

    const handleDeleteItem = async (itemId: number) => {
        try {
            await InventoryService.deleteItem(itemId);
            // Functional update guards against stale closures: the awaited delete may
            // span renders, after which `items` in this closure is older than state.
            setItems(prev => prev.filter(i => i.id !== itemId));
            toast.success('Item deleted successfully');
        } catch (err) {
            console.error('Failed to delete item:', err);
            toast.error('Failed to delete item');
        }
    };

    const confirmDelete = (item: Item) => {
        setItemToDelete(item);
        setIsDeleteModalOpen(true);
        setActionMenuItemId(null);
    };

    const openAddModal = () => {
        navigate('/inventory/new');
    };

    const openEditModal = (item: Item) => {
        navigate(`/inventory/edit/${item.id}`);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
            </div>
        );
    }

    // ─── List / Grid View ───────────────────────────────────────
    return (
        <div className="space-y-8 animate-fade-in">
            {/* Modals */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => { setIsDeleteModalOpen(false); setItemToDelete(null); }}
                onConfirm={() => {
                    if (itemToDelete) handleDeleteItem(itemToDelete.id);
                    setIsDeleteModalOpen(false);
                    setItemToDelete(null);
                }}
                title="Delete Asset"
                message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                isDanger
            />

            {/* ── Editorial Header ── */}
            <header className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                <div>
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="text-3xl md:text-4xl font-headline font-extrabold tracking-tight text-[var(--primary)]">
                            Inventory &amp; Assets
                        </h1>
                        <span className="inline-flex items-center gap-2.5 px-3 py-1 rounded-full bg-[var(--bg-hover)] leading-none">
                            <span className="inline-flex items-baseline gap-1.5">
                                <span className="text-lg font-bold tabular-nums text-[var(--primary)]">{items.length}</span>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                                    {items.length === 1 ? 'item' : 'items'}
                                </span>
                            </span>
                            <span aria-hidden="true" className="w-px h-3.5 bg-[var(--border)]" />
                            <span className="inline-flex items-baseline gap-1.5">
                                <span className="text-lg font-bold tabular-nums text-[var(--primary)]">
                                    ${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                                    valuation
                                </span>
                            </span>
                        </span>
                    </div>
                    <p className="text-[var(--text-secondary)] font-medium mt-1">
                        Catalogue and track your home&apos;s valuable components.
                    </p>
                </div>
                <button
                    onClick={openAddModal}
                    className="flex items-center gap-2 px-6 py-3 bg-[var(--primary)] text-white rounded-full font-semibold hover:opacity-90 transition-opacity active:scale-95 duration-200"
                >
                    <Plus size={18} />
                    New Asset
                </button>
            </header>

            {/* ── Search + Filter Bar ── */}
            <section className="flex flex-wrap items-center gap-3 md:gap-4 bg-[var(--bg-hover)] p-3 md:p-4 rounded-xl">
                {/* Search */}
                <div className="relative flex-grow max-w-md">
                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                    <input
                        className="w-full bg-[var(--bg-card)] rounded-lg pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-[var(--primary-container)] outline-none text-[var(--text-main)] placeholder-[var(--text-muted)]"
                        placeholder="Search by name, category, or serial..."
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-main)]">
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* Divider */}
                <div className="hidden md:block h-10 w-px bg-[var(--border)]" />

                {/* Category Pills */}
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                                selectedCategory === cat
                                    ? 'bg-[var(--primary)] text-white'
                                    : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]'
                            }`}
                        >
                            {formatCategory(cat)}
                        </button>
                    ))}
                </div>

                {/* View Toggle */}
                <div className="ml-auto flex items-center gap-1 bg-[var(--bg-elevated-high)] p-1 rounded-lg">
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-[var(--bg-card)] text-[var(--primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card)]'}`}
                        aria-label="List view"
                    >
                        <List size={18} />
                    </button>
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-[var(--bg-card)] text-[var(--primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card)]'}`}
                        aria-label="Grid view"
                    >
                        <LayoutGrid size={18} />
                    </button>
                </div>
            </section>

            {/* ── Error State ── */}
            {error && (
                <div className="p-4 bg-[var(--error-container)] text-[var(--error)] rounded-xl text-sm font-medium">
                    {error}
                </div>
            )}

            {/* ── Empty State ── */}
            {filteredItems.length === 0 && !loading && (
                <div className="text-center py-16">
                    <Package size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
                    <h3 className="text-lg font-headline font-bold text-[var(--text-main)] mb-2">
                        {searchQuery || selectedCategory !== 'All' ? 'No items found' : 'No assets yet'}
                    </h3>
                    <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
                        {searchQuery || selectedCategory !== 'All'
                            ? 'Try adjusting your search or filters'
                            : 'Start by adding your first asset to the inventory'}
                    </p>
                    {!searchQuery && selectedCategory === 'All' && (
                        <button
                            onClick={openAddModal}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--primary)] text-white rounded-full font-semibold hover:opacity-90 transition"
                        >
                            <Plus size={18} />
                            Add Your First Asset
                        </button>
                    )}
                </div>
            )}

            {/* ── Data Table (List View) ── */}
            {filteredItems.length > 0 && viewMode === 'list' && (
                <div className="bg-[var(--bg-card)] rounded-2xl overflow-hidden shadow-[var(--shadow-sm)]">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-[var(--bg-hover)]">
                                    <th className="px-6 md:px-8 py-5 text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">Asset Item</th>
                                    <th className="hidden lg:table-cell px-6 py-5 text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">Status</th>
                                    <th className="hidden md:table-cell px-6 py-5 text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">Category</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">Valuation</th>
                                    <th className="hidden xl:table-cell px-6 py-5 text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">Documents</th>
                                    <th className="px-6 md:px-8 py-5 text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--bg-hover)]">
                                {paginatedItems.map((item) => {
                                    const Icon = getCategoryIcon(item.item_category || item.category);
                                    const imageUrl = getItemImageUrl(item);
                                    const statuses = getItemStatus(item);
                                    const docs = getDocumentIndicators(item);
                                    const value = getItemValueNum(item);

                                    return (
                                        <tr
                                            key={item.id}
                                            data-testid="item-card"
                                            className="hover:bg-[color-mix(in_srgb,var(--bg-elevated),transparent_70%)] transition-colors group cursor-pointer"
                                            onClick={() => navigate(`/inventory/${item.id}`)}
                                        >
                                            {/* Asset Item */}
                                            <td className="px-6 md:px-8 py-5">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[var(--bg-hover)] overflow-hidden flex-shrink-0 flex items-center justify-center">
                                                        {imageUrl ? (
                                                            <img src={imageUrl} alt={item.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <Icon size={24} className="text-[var(--text-muted)]" />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="font-bold text-[var(--primary)] truncate">{item.name}</p>
                                                        <p className="text-xs text-[var(--text-secondary)] truncate">
                                                            {item.serial_number ? `SN: ${item.serial_number}` : (item.manufacturer || formatCategory(item.item_category || item.category))}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="hidden lg:table-cell px-6 py-5">
                                                <div className="flex gap-2 flex-wrap">
                                                    {statuses.map((s, i) => (
                                                        <Badge key={i} label={s.label} variant={s.variant} />
                                                    ))}
                                                </div>
                                            </td>

                                            {/* Category + Room */}
                                            <td className="hidden md:table-cell px-6 py-5">
                                                <p className="text-sm font-medium text-[var(--text-main)]">{formatCategory(item.item_category || item.category)}</p>
                                                <p className="text-xs text-[var(--text-secondary)]">{getRoomName(item.room_id)}</p>
                                            </td>

                                            {/* Valuation */}
                                            <td className="px-6 py-5 font-bold text-[var(--primary)] whitespace-nowrap">
                                                {value > 0 ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                            </td>

                                            {/* Documents */}
                                            {/* lucide-react icons don't honor a `title` prop (it isn't in
                                                LucideProps), so the original title="…" tooltips never rendered.
                                                Use aria-label, which is valid on the SVG and announced by
                                                screen readers — surfaced by the TS migration. */}
                                            <td className="hidden xl:table-cell px-6 py-5">
                                                <div className="flex gap-3 text-[var(--text-secondary)]">
                                                    <BookOpen size={18} className={`${docs.manual ? 'opacity-100 cursor-help' : 'opacity-20'}`} aria-label={docs.manual ? 'Manual Available' : 'No Manual'} />
                                                    <Receipt size={18} className={`${docs.receipt ? 'opacity-100 cursor-help' : 'opacity-20'}`} aria-label={docs.receipt ? 'Receipt Filed' : 'No Receipt'} />
                                                    <ShieldCheck size={18} className={`${docs.warranty ? 'opacity-100 cursor-help' : 'opacity-20'}`} aria-label={docs.warranty ? 'Warranty Active' : 'No Warranty Doc'} />
                                                </div>
                                            </td>

                                            {/* Actions */}
                                            <td className="px-6 md:px-8 py-5 text-right">
                                                <div className="relative inline-block" data-action-menu>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActionMenuItemId(actionMenuItemId === item.id ? null : item.id);
                                                        }}
                                                        className="p-2 rounded-lg hover:bg-[var(--bg-elevated)] transition-colors text-[var(--text-secondary)] group-hover:text-[var(--primary)]"
                                                        aria-label="Item actions"
                                                    >
                                                        <MoreHorizontal size={18} />
                                                    </button>
                                                    {actionMenuItemId === item.id && (
                                                        <div className="absolute right-0 top-full mt-1 bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow)] border border-[var(--border)] py-1 z-20 min-w-[160px]">
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); navigate(`/inventory/${item.id}`); setActionMenuItemId(null); }}
                                                                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--text-main)] hover:bg-[var(--bg-hover)] transition-colors"
                                                            >
                                                                <Eye size={15} /> View Details
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); openEditModal(item); }}
                                                                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--text-main)] hover:bg-[var(--bg-hover)] transition-colors"
                                                            >
                                                                <Edit2 size={15} /> Edit Asset
                                                            </button>
                                                            <div className="border-t border-[var(--border)] my-1" />
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); confirmDelete(item); }}
                                                                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--error)] hover:bg-[var(--error-container)] transition-colors"
                                                            >
                                                                <Trash2 size={15} /> Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <footer className="px-6 md:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[color-mix(in_srgb,var(--bg-hover),transparent_50%)]">
                        <p className="text-xs font-medium text-[var(--text-secondary)]">
                            Showing {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, filteredItems.length)} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length} assets
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="w-10 h-10 flex items-center justify-center rounded-lg bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                let pageNum: number;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-10 h-10 flex items-center justify-center rounded-lg font-bold text-sm transition-colors ${
                                            currentPage === pageNum
                                                ? 'bg-[var(--primary)] text-white'
                                                : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="w-10 h-10 flex items-center justify-center rounded-lg bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </footer>
                </div>
            )}

            {/* ── Grid View ── */}
            {filteredItems.length > 0 && viewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems.map((item) => {
                        const Icon = getCategoryIcon(item.item_category || item.category);
                        const imageUrl = getItemImageUrl(item);
                        const value = getItemValueNum(item);
                        const statuses = getItemStatus(item);

                        return (
                            <Card
                                key={item.id}
                                data-testid="item-card"
                                onClick={() => navigate(`/inventory/${item.id}`)}
                                className="p-0 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                            >
                                {/* Image / Icon Header */}
                                <div className="h-36 bg-[var(--bg-hover)] flex items-center justify-center">
                                    {imageUrl ? (
                                        <img src={imageUrl} alt={item.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <Icon size={40} className="text-[var(--text-muted)]" />
                                    )}
                                </div>

                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-bold text-[var(--primary)] truncate">{item.name}</h3>
                                            <p className="text-xs text-[var(--text-secondary)] truncate mt-0.5">
                                                {item.manufacturer || formatCategory(item.item_category || item.category)}
                                            </p>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); openEditModal(item); }}
                                            aria-label="Edit asset"
                                            className="p-1.5 text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors flex-shrink-0"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                    </div>

                                    {/* Status badges */}
                                    <div className="flex gap-1.5 flex-wrap mb-3">
                                        {statuses.map((s, i) => (
                                            <Badge key={i} label={s.label} variant={s.variant} />
                                        ))}
                                    </div>

                                    <div className="space-y-1.5 text-sm">
                                        <div className="flex justify-between text-[var(--text-secondary)]">
                                            <span>Location</span>
                                            <span className="font-medium text-[var(--text-main)]">{getRoomName(item.room_id)}</span>
                                        </div>
                                        {value > 0 && (
                                            <div className="flex justify-between text-[var(--text-secondary)]">
                                                <span>Value</span>
                                                <span className="font-bold text-[var(--primary)]">${value.toLocaleString()}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Inventory;
